import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { generatePath } from 'react-router-dom';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material';

import KeyboardArrowDownIcon from '../../../assets/icons/KeyboardArrowDownIcon';
import TextLink from '../../../components/TextLink/TextLink';
import canLinkWorkerToUserDetails from '../../../components/WorkerNameLink/canLinkWorkerToUserDetails';
import selectUser from '../../../store/selectors/appSelector';
import { BLACK, PRIMARY_COLOR, PRIMARY_PURPLE } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import { ROUTE_CONSTANTS } from '../../app/RouteBuilding/routeConstants';

import WorkerTimecard from './WorkerTimecard';

const getCandidateAgencyId = (candidate) => {
  const agency = candidate?.agency;
  if (agency == null || agency === '') return undefined;
  if (typeof agency === 'object') return agency.ID ?? agency._id;
  return agency;
};

const ViewUserDetailsLink = ({ candidate }) => {
  const user = useSelector(selectUser);
  const userId = candidate?._id;
  const canLink = canLinkWorkerToUserDetails({
    role: user?.role,
    userId,
    workerAgencyId: getCandidateAgencyId(candidate),
    recruiterAgencyId: user?.recruiter?.agency?.ID,
    workerName: candidate?.name,
  });

  if (!canLink) return null;

  return (
    <TextLink
      to={generatePath(`/${ROUTE_CONSTANTS.USERS_V2_DETAIL}`, { id: String(userId) })}
      sx={{
        alignSelf: 'flex-start',
        color: PRIMARY_PURPLE[70],
        cursor: 'pointer',
        fontSize: '16px',
        marginLeft: '42px',
        textDecoration: 'underline',
      }}
    >
      {LanguageConverterV2('timekeeping.workerTimecard.viewUserDetails')}
    </TextLink>
  );
};

ViewUserDetailsLink.propTypes = {
  candidate: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    agency: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ]),
  }),
};

const WorkerTimecardSet = ({
  candidateTimecards,
  total,
  isLoading,
  isDisputedTimecardUpdating,
  currentPage,
  pageSize,
  onPaginationChange,
  candidateCheckedMap,
  setCandidateCheckedMap,
}) => {
  const [expandedItems, setExpandedItems] = useState([]);
  const allIds = (candidateTimecards || []).map((t) => t.candidate._id).filter(Boolean);

  // Create a stable string representation of candidate IDs to track when the candidate list changes
  // This only changes when the actual list of candidates changes, not when their timecard data updates
  const candidateIdsKey = useMemo(
    () =>
      (candidateTimecards || [])
        .map((t) => t.candidate._id)
        .filter(Boolean)
        .join(','),
    [candidateTimecards]
  );

  // Reset expanded items only when the candidate list changes (pagination, new candidates)
  // but not when timecard data within existing candidates is updated
  useEffect(() => {
    setExpandedItems([]);
  }, [candidateIdsKey, pageSize, currentPage]);

  const handleOpenCloseAllToggle = () => {
    // Check if all current page items are expanded
    const allCurrentPageExpanded = allIds.every((id) => expandedItems.includes(id));

    if (allCurrentPageExpanded) {
      // Close all current page items
      setExpandedItems((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      // Open all current page items
      setExpandedItems((prev) => [...new Set([...prev, ...allIds])]);
    }
  };

  const handleAccordionChange = (id) => (_, isExpanded) => {
    setExpandedItems((prev) =>
      isExpanded ? [...prev, id] : prev.filter((itemId) => itemId !== id)
    );
  };

  const handleChangePage = (event, newPage) => {
    onPaginationChange(newPage, pageSize);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    onPaginationChange(0, newRowsPerPage);
  };

  const handleCandidateCheck = (candidateId, timecards, checked) => {
    const updatedTimecardMap = { ...candidateCheckedMap };

    timecards.forEach((day) => {
      const key = `${candidateId}_${day.placementId}`;
      updatedTimecardMap[key] = checked;
    });

    setCandidateCheckedMap((prev) => ({
      ...prev,
      ...updatedTimecardMap,
      [candidateId]: checked,
    }));
  };

  const handleTimecardCheck = (candidateId, key, checked, timecards) => {
    const updatedTimecardMap = {
      ...candidateCheckedMap,
      [key]: checked,
    };

    const allChecked = timecards.every(
      (day) => updatedTimecardMap[`${candidateId}_${day.placementId}`]
    );

    setCandidateCheckedMap((prev) => ({
      ...prev,
      ...updatedTimecardMap,
      [candidateId]: allChecked,
    }));
  };

  // Check if all items on the current page are expanded
  const isAllOpen = allIds.length > 0 && allIds.every((id) => expandedItems.includes(id));

  const accordions = candidateTimecards?.map((candidateTimecard) => {
    const id = candidateTimecard.candidate._id;
    return (
      <Accordion
        key={id}
        expanded={expandedItems.includes(id)}
        onChange={handleAccordionChange(id)}
      >
        <AccordionSummary
          aria-controls="department-content"
          id="department-header"
          expandIcon={<KeyboardArrowDownIcon />}
          sx={{ background: BLACK[10] }}
        >
          <Box display="flex" alignItems="center" flexGrow={1}>
            <Typography
              style={{
                color: PRIMARY_COLOR[70],
                fontFamily: PRIMARY_FONT[500],
                fontWeight: '600',
                marginRight: '10px',
                fontSize: '24px',
              }}
            >
              <Checkbox
                checked={candidateCheckedMap[candidateTimecard?.candidate?._id] ?? false}
                onChange={(e) =>
                  handleCandidateCheck(
                    candidateTimecard?.candidate?._id,
                    candidateTimecard.timecards,
                    e.target.checked
                  )
                }
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
              />
              {candidateTimecard?.candidate?.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Typography
              sx={{
                fontFamily: PRIMARY_FONT[400],
                alignSelf: 'center',
                fontSize: '16px',
                marginRight: '12px',
              }}
            >
              {LanguageConverterV2('timekeeping.workerTimecard.otHours')}:
              <span style={{ fontFamily: PRIMARY_FONT[500] }}>
                {candidateTimecard?.overtimeHours?.toFixed(2)}
              </span>
            </Typography>
            <Typography
              sx={{
                fontFamily: PRIMARY_FONT[400],
                alignSelf: 'center',
                fontSize: '16px',
                marginRight: '12px',
              }}
            >
              {LanguageConverterV2('timekeeping.overviewCard.totalHours')}:
              <span style={{ fontFamily: PRIMARY_FONT[500] }}>
                {candidateTimecard?.totalHours?.toFixed(2)}
              </span>
            </Typography>
            <Typography
              sx={{
                fontFamily: PRIMARY_FONT[400],
                alignSelf: 'center',
                fontSize: '14px',
                textTransform: 'uppercase',
                marginRight: '12px',
              }}
            >
              {LanguageConverterV2('button.close')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack gap="16px">
            {expandedItems.includes(id) && (
              <ViewUserDetailsLink candidate={candidateTimecard.candidate} />
            )}
            {expandedItems.includes(id) &&
              candidateTimecard?.timecards.map((day) => {
                const key = `${candidateTimecard.candidate._id}_${day.placementId}`;
                return (
                  <WorkerTimecard
                    key={day.placementId}
                    isChecked={candidateCheckedMap[key] ?? false}
                    onTimecardChecked={(checked) =>
                      handleTimecardCheck(
                        candidateTimecard.candidate._id,
                        key,
                        checked,
                        candidateTimecard.timecards
                      )
                    }
                    weekday={day.start}
                    jobName={day.jobName}
                    candidateName={candidateTimecard?.candidate?.name}
                    shiftStart={day.start}
                    shiftEnd={day.end}
                    timecard={day.timecard}
                    gps={day?.gps}
                    attendance={day?.attendance}
                    placementId={day?.placementId}
                    corporationId={day?.corporation?._id}
                    jobId={day?.job?.jobId}
                    isDisputedTimecardUpdating={isDisputedTimecardUpdating}
                  />
                );
              })}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  });

  const content = (
    <Stack width="100%">
      {candidateTimecards && (
        <Box display="flex" justifyContent="end">
          <Typography
            onClick={handleOpenCloseAllToggle}
            sx={{
              width: '112px',
              border: '1px solid #ccc',
              padding: '6px 24px',
              borderTopLeftRadius: '5px',
              borderTopRightRadius: '5px',
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            {isAllOpen
              ? LanguageConverterV2('timekeeping.timecardList.closeAll')
              : LanguageConverterV2('timekeeping.timecardList.openAll')}
          </Typography>
        </Box>
      )}
      <Stack gap="12px">{accordions}</Stack>
    </Stack>
  );

  return (
    <Stack width="100%">
      {isLoading ? (
        <Box
          sx={{ height: '640px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography>Loading...</Typography>
        </Box>
      ) : (
        <>
          {content}
          <TablePagination
            component="div"
            count={total}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 15, 25]}
            labelRowsPerPage={LanguageConverterV2('pagination.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) =>
              LanguageConverterV2('pagination.displayedRows', { from, to, count })
            }
            sx={{
              '& .MuiTablePagination-toolbar': {
                fontSize: '16px',
              },
              '& .MuiTablePagination-selectLabel': {
                fontSize: '16px',
              },
              '& .MuiTablePagination-displayedRows': {
                fontSize: '16px',
              },
              '& .MuiTablePagination-select': {
                fontSize: '16px',
              },
            }}
          />
        </>
      )}
    </Stack>
  );
};

WorkerTimecardSet.propTypes = {
  candidateTimecards: PropTypes.arrayOf(PropTypes.shape({})),
  total: PropTypes.number,
  isLoading: PropTypes.bool,
  isDisputedTimecardUpdating: PropTypes.bool,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  onPaginationChange: PropTypes.func,
  candidateCheckedMap: PropTypes.shape({}),
  setCandidateCheckedMap: PropTypes.func,
};

export default WorkerTimecardSet;
